import LiquidGlassCard from "@/components/landing/LiquidGlassCard"
import { LEDHeartbeat } from "@/components/ui/living-brand"
import { SFIcon } from "@/components/ui/sf-icon"
import { Heading, Text } from "@/components/ui/typography"
import { PROVIDER_COVERAGE } from "@/lib/providerCoverage"
import { sfIcons } from '@/lib/sf-icons'
import { cn } from "@/lib/utils"
import { semanticTokens } from "@supabank/config/theme"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

export function TrustComplianceSection({ id }: { id?: string }) {
  const { t } = useTranslation("marketing")

  // Security features from SSOT (marketing.json)
  const securityFeatures = (t("trustCompliance.features", { returnObjects: true }) as any[]) || []
  return (
    <section
      id={id}
      aria-labelledby="promo-trust-title"
      className={cn(
        "relative py-16 sm:py-24 overflow-hidden",
        "outline-none", // Remove any focus outline (section is not interactive)
        "bg-slate-50/50 dark:bg-slate-950/50"
      )}
      tabIndex={-1} // Prevent focus outline (section is not interactive)
    >
      {/* Color Lab: Premium dark gradient for security emphasis */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse at 50% 100%, hsla(220, 15%, 13%, 0.15) 0%, transparent 60%),
            radial-gradient(ellipse at 0% 50%, hsla(270, 95%, 75%, 0.06) 0%, transparent 40%)
          `
        }}
      />
      <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6">
        <div className="grid grid-cols-1 gap-8 sm:gap-10 md:gap-12 lg:grid-cols-2 lg:gap-16 items-start">
          {/* Left: Content */}
          <div className="space-y-6 sm:space-y-8 md:space-y-10">
            <div className="space-y-4 sm:space-y-5 md:space-y-6">
              {/* Canon v1.4.5: Premium State for security emphasis */}
              <div className={cn(
                "inline-flex items-center gap-2 px-3 py-1 rounded-full font-bold uppercase tracking-wide",
                semanticTokens.typography.captionSmall,
                semanticTokens.premium.bg,
                semanticTokens.premium.fg
              )}>
                <sfIcons.security className="w-3 h-3" />
                {t("trustCompliance.badge")}
              </div>
              <Heading level={2} id="promo-trust-title" className="leading-tight">
                {t("trustCompliance.headline")}
              </Heading>
              <Text variant="lg" color="muted" className="leading-relaxed">
                {t("trustCompliance.description")}
              </Text>
            </div>

            <div className="space-y-4 sm:space-y-5 md:space-y-6">
              {securityFeatures.map((item, i) => (
                <div key={i} className="flex gap-3 sm:gap-4">
                  {/* Canon v1.4.5: Premium accent for security features */}
                  <div className={cn(
                    "mt-1 flex h-6 w-6 items-center justify-center rounded-full font-bold shrink-0",
                    semanticTokens.typography.captionSmall,
                    semanticTokens.premium.bg,
                    semanticTokens.premium.accent
                  )}>
                    {i + 1}
                  </div>
                  <div className="min-w-0">
                    <Heading level={4}>{item.title}</Heading>
                    <Text variant="sm" color="muted" className="leading-relaxed">{item.description}</Text>
                  </div>
                </div>
              ))}
            </div>

            <Text variant="caption" color="muted" className="italic pt-2">
              {t("trustCompliance.closing")}
            </Text>
          </div>

          {/* Right: Live Status Card + Radical Transparency */}
          <div className="space-y-6">
            <LiquidGlassCard
              tier="surface"
            >
              <div className="p-6 sm:p-8">
                <div className="space-y-4 sm:space-y-5">
                  <div className="flex items-center gap-2">
                    <LEDHeartbeat size={6} showGlow={false} />
                    <Text variant="caption" color="muted" className="uppercase tracking-widest">
                      {t("trustCompliance.liveStatus.label")}
                    </Text>
                  </div>
                  <div className="space-y-3">
                    <Heading level={3}>
                      {t("trustCompliance.liveStatus.countries")}
                    </Heading>
                    <div className="flex flex-wrap gap-2">
                      <span className={cn(
                        "px-3 py-1 rounded-full text-sm font-medium",
                        semanticTokens.info.bg,
                        semanticTokens.info.fg
                      )}>
                        {t("trustCompliance.liveStatus.currencies").replace("{count}", String(PROVIDER_COVERAGE.currenciesCount))}
                      </span>
                      <span className={cn(
                        "px-3 py-1 rounded-full text-sm font-medium",
                        semanticTokens.info.bg,
                        semanticTokens.info.fg
                      )}>
                        {t("trustCompliance.liveStatus.stablecoinPairs").replace("{count}", String(PROVIDER_COVERAGE.supportedPairs.length))}
                      </span>
                    </div>
                  </div>
                  <div className={cn(
                    "pt-4 border-t",
                    semanticTokens.border.subtle
                  )}>
                    <Text variant="caption" color="muted" className="leading-relaxed">
                      {t("trustCompliance.liveStatus.note")}
                    </Text>
                  </div>
                </div>
              </div>
            </LiquidGlassCard>

            {/* Radical Transparency Card */}
            <LiquidGlassCard
              tier="surface"
            >
              <div className="p-6 sm:p-8">
                <div className="space-y-4 sm:space-y-5">
                  <Text variant="caption" color="muted" className="uppercase tracking-widest font-medium">
                    {t("trustCompliance.radicalTransparency.badge")}
                  </Text>
                  <Heading level={3}>
                    {t("trustCompliance.radicalTransparency.headline")}
                  </Heading>

                  {/* Who we are */}
                  <div className="space-y-2">
                    <Text variant="sm" className="font-semibold">{t("trustCompliance.radicalTransparency.whoWeAreLabel")}</Text>
                    <Text variant="sm" color="muted" className="leading-relaxed">
                      {t("trustCompliance.radicalTransparency.whoWeAre.entity")}
                      <br />
                      {t("trustCompliance.radicalTransparency.whoWeAre.location")}
                      <br />
                      <span className="italic">{t("trustCompliance.radicalTransparency.whoWeAre.legal")}</span>
                    </Text>
                  </div>

                  {/* What we DON'T do */}
                  <div className={cn("pt-4 border-t", semanticTokens.border.subtle)}>
                    <Text variant="sm" className="font-semibold mb-3">{t("trustCompliance.radicalTransparency.whatWeDontLabel")}</Text>
                    <ul className="space-y-2">
                      {((t("trustCompliance.radicalTransparency.whatWeDont", { returnObjects: true }) as string[]) || []).map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <sfIcons.close className={cn("h-4 w-4 mt-0.5 flex-shrink-0", semanticTokens.fg.destructive)} aria-hidden="true" />
                          <Text variant="sm" color="muted" as="span">{item}</Text>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* What we DO */}
                  <div className={cn("pt-4 border-t", semanticTokens.border.subtle)}>
                    <Text variant="sm" className="font-semibold mb-3">{t("trustCompliance.radicalTransparency.whatWeDoLabel")}</Text>
                    <ul className="space-y-2">
                      {((t("trustCompliance.radicalTransparency.whatWeDo", { returnObjects: true }) as string[]) || []).map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <sfIcons.check className={cn("h-4 w-4 mt-0.5 flex-shrink-0", semanticTokens.accent.primary)} aria-hidden="true" />
                          <Text variant="sm" color="muted" as="span">{item}</Text>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </LiquidGlassCard>

            {/* Limitations & Compliance Block (P1 Landing Blocks) */}
            <LiquidGlassCard
              tier="surface"
            >
              <div className="p-6 sm:p-8">
                <div className="space-y-4 sm:space-y-5">
                  <div className="flex items-center gap-2">
                    <SFIcon icon={sfIcons.alertCircle} size="sm" variant="default" className={cn(semanticTokens.warning.accent)} />
                    <Text variant="caption" color="muted" className="uppercase tracking-widest font-medium">
                      {t("trustCompliance.limitations.badge")}
                    </Text>
                  </div>
                  <Heading level={3}>
                    {t("trustCompliance.limitations.headline")}
                  </Heading>
                  <Text variant="sm" color="muted" className="leading-relaxed">
                    {t("trustCompliance.limitations.description")}
                  </Text>

                  {/* Limitations List */}
                  <ul className="space-y-2 pt-2">
                    {((t("trustCompliance.limitations.items", { returnObjects: true }) as string[]) || []).map((item, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <SFIcon
                          icon={sfIcons.alertCircle}
                          size="sm"
                          variant="default"
                          className={cn("h-4 w-4 mt-0.5 flex-shrink-0", semanticTokens.warning.accent)}
                          aria-hidden="true"
                        />
                        <Text variant="sm" color="muted" as="span" className="leading-relaxed">{item}</Text>
                      </li>
                    ))}
                  </ul>

                  {/* Compliance Links */}
                  <div className={cn("pt-4 border-t", semanticTokens.border.subtle)}>
                    <div className="flex flex-wrap gap-4">
                      {t("trustCompliance.limitations.legalInfoHref") && (
                        <Link
                          to={t("trustCompliance.limitations.legalInfoHref")}
                          className={cn(
                            "inline-flex items-center gap-1.5 text-sm font-medium",
                            semanticTokens.info.accent,
                            "hover:underline",
                            semanticTokens.focus.ring,
                            "min-h-[44px] touch-manipulation"
                          )}
                        >
                          {t("trustCompliance.limitations.legalInfoLabel")}
                          <sfIcons.externalLink className="h-3 w-3" aria-hidden="true" />
                        </Link>
                      )}
                      {t("trustCompliance.limitations.securityPolicyHref") && (
                        <Link
                          to={t("trustCompliance.limitations.securityPolicyHref")}
                          className={cn(
                            "inline-flex items-center gap-1.5 text-sm font-medium",
                            semanticTokens.info.accent,
                            "hover:underline",
                            semanticTokens.focus.ring,
                            "min-h-[44px] touch-manipulation"
                          )}
                        >
                          {t("trustCompliance.limitations.securityPolicyLabel")}
                          <sfIcons.externalLink className="h-3 w-3" aria-hidden="true" />
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </LiquidGlassCard>
          </div>
        </div>
      </div>
    </section>
  )
}
